import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router';

import { getFilterExpression } from '../forms/genericSearchTermFilter';
import { FilterValues, ReportFilter } from '../types/filters';
import { ReportGridGroup, ReportGridItem } from '../types/report';
import { updateQueriesWithFilters } from '../utils/filters';

export type UseFiltersResponse = {
  clearFilters?: () => void;
  filterValues: FilterValues;
  groupsWithFilters: ReportGridGroup[];
  handleFilterChange: (filter: string) => (value: string | null) => void;
  itemsWithFilters: ReportGridItem[];
};

export const useFilters = ({
  filters = [],
  groups = [],
  items,
  onFiltersChange,
}: {
  filters?: ReportFilter[];
  groups?: ReportGridGroup[];
  items?: ReportGridItem[];
  onFiltersChange?: (filterValues: FilterValues) => void;
}): UseFiltersResponse => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultValues = Array.isArray(filters)
    ? Object.fromEntries(
        filters
          .map((filter) => {
            if (filter && filter.defaultValue && filter.filterExpression) {
              return [
                filter.filterLabel,
                getFilterExpression(filter?.filterExpression, filter.defaultValue),
              ];
            }

            return [filter.filterLabel, null];
          })
          .filter(([_, value]) => value !== null),
      )
    : {};

  const [filterValues, setFilterValues] = useState<FilterValues>({
    ...defaultValues,
    ...Object.fromEntries(searchParams.entries()),
  });

  useEffect(() => {
    onFiltersChange?.(filterValues);
  }, [filterValues, onFiltersChange]);

  const handleFilterChange = (filter: string) => (value: string | null) => {
    setFilterValues((prev) => ({ ...prev, [filter]: value }));
    setSearchParams((prev) => {
      if (value) {
        prev.set(filter, value);
      } else {
        prev.delete(filter);
      }

      return prev;
    });
  };

  const clearFilters = () => {
    setFilterValues({});
    setSearchParams({});
  };

  const itemsWithFilters = useMemo(() => {
    try {
      return updateQueriesWithFilters(items ?? [], filters, filterValues);
    } catch {
      return items ?? [];
    }
  }, [items, filters, filterValues]);

  const groupsWithFilters = useMemo(() => {
    try {
      return (
        groups?.map((group) => ({
          ...group,
          items: updateQueriesWithFilters(group.items ?? [], filters, filterValues),
        })) ?? []
      );
    } catch {
      return groups ?? [];
    }
  }, [groups, filters, filterValues]);

  return {
    clearFilters: Object.keys(filterValues).length > 0 ? clearFilters : undefined,
    filterValues,
    groupsWithFilters,
    handleFilterChange,
    itemsWithFilters,
  };
};
